/**
 * What a 402 from the backend means here.
 *
 * `requireSubscription` in the backend answers **402 Payment Required** with
 * `error.code = "SUBSCRIPTION_REQUIRED"` and the tiers in `data`. Before the
 * 2026-09-17 audit almost no paid route carried that check, so the status never
 * arrived and nothing in the frontend knew it: a FREE account reaching a paid
 * page would have fallen into the default branch of the error handler and been
 * shown "Error 402".
 *
 * The clients announce it instead, and one listener - the upgrade dialog
 * mounted in App - turns it into the offer to upgrade. Going through an event
 * rather than each call site means a route that gets its paywall later is
 * covered on the day the backend starts answering 402.
 */

import { HttpError } from "@/lib/http";

export type PaidTier = "PLUS" | "PREMIUM";

export interface PaymentRequired {
  /** The tier the route is sold at. */
  requiredTier: PaidTier;
  /** What the account holds now - FREE, or a paid tier that is not paid up. */
  currentTier: string;
  /** The Stripe subscription status, e.g. "none", "past_due". */
  status: string;
}

export const PAYMENT_REQUIRED_EVENT = "healthpass:payment-required";

/**
 * Reads a 402 out of a failed request, or null when it is any other failure.
 *
 * The tier is taken from the body when it is there and defaults to PLUS, which
 * is the lowest paid tier: an upgrade prompt naming too small a plan would send
 * the user to a checkout that still does not unlock the page.
 */
export function paymentRequired(error: unknown): PaymentRequired | null {
  if (!(error instanceof HttpError)) return null;
  if (error.response?.status !== 402) return null;

  const body = error.response.data as
    | {
        error?: { code?: string };
        data?: {
          requiredTier?: string;
          currentTier?: string;
          status?: string;
        };
      }
    | undefined;

  const payload = body?.data;
  const required = payload?.requiredTier === "PREMIUM" ? "PREMIUM" : "PLUS";

  return {
    requiredTier: required,
    currentTier: payload?.currentTier ?? "FREE",
    status: payload?.status ?? "none",
  };
}

/** Announce a 402 so the upgrade dialog can offer the plan that unlocks it. */
export function announcePaymentRequired(detail: PaymentRequired): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<PaymentRequired>(PAYMENT_REQUIRED_EVENT, { detail }),
  );
}

/**
 * Request option for a caller that makes the upgrade offer itself.
 *
 * Some paid content sits inside a free flow - the health check in the trip
 * wizard, the health section of a trip's page. There a 402 is an expected
 * answer that the page turns into its own, optional offer; a modal on top of
 * it would block a flow the user does not have to pay for. Passing
 * `{ [QUIET_PAYWALL]: true }` in the request config keeps the dialog shut. The
 * error is still thrown, so the caller sees the 402.
 */
export const QUIET_PAYWALL = "quietPaywall";

function isQuietPaywall(error: unknown): boolean {
  return error instanceof HttpError && error.config?.[QUIET_PAYWALL] === true;
}

/**
 * The whole handling for one failed request: announce it if it was a 402,
 * unless the request asked to make the offer itself. Returns whether it was a
 * 402, so an error handler can skip its generic message.
 */
export function handlePaymentRequired(error: unknown): boolean {
  const detail = paymentRequired(error);
  if (!detail) return false;
  if (!isQuietPaywall(error)) announcePaymentRequired(detail);
  return true;
}
